import { useState } from 'react';
import { type ParsedApiError } from '@loctopus/shared';
import { ErrorSummary } from '@loctopus/ui';
import { api, type TotpEnrollmentStart } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';
import { Button, Card, FormField, Input, PageHeader } from '../../components/ui';
import { useAuth } from '../../lib/auth';

/**
 * Self-service 2FA for tenant staff (docs/WALKTHROUGH.md "2FA" point 5) - optional by default
 * (unless a Super Admin forced it per-tenant, in which case it's already enrolled by the time
 * staff can reach this page at all). "Enable" and "Reset" are the exact same server operation
 * (`POST /dashboard/auth/totp/reset`) - only the label/copy differs based on current status.
 */
export function SecurityPage() {
  const { totpEnabled, refresh } = useAuth();
  const [step, setStep] = useState<'idle' | 'enroll' | 'recovery-codes'>('idle');
  const [enrollment, setEnrollment] = useState<TotpEnrollmentStart | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [error, setError] = useState<ParsedApiError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function start() {
    setError(null);
    setSubmitting(true);
    try {
      // No reauthToken passed explicitly - request()'s reactive retry (lib/api.ts) prompts for
      // one automatically if the server comes back with reauthRequired.
      const started = await api.auth.totpResetStart();
      setEnrollment(started);
      setCode('');
      setStep('enroll');
    } catch (err) {
      if (!(err instanceof Error) || err.message !== 'Re-authentication was cancelled.') {
        setError(toParsedError(err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function confirm() {
    setError(null);
    setSubmitting(true);
    try {
      const result = await api.auth.totpResetConfirm(code);
      setRecoveryCodes(result.recoveryCodes);
      setStep('recovery-codes');
      await refresh();
    } catch (err) {
      setError(toParsedError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Security"
        description="Manage two-factor authentication for your own account."
      />
      <Card title="Two-factor authentication">
        {step === 'idle' && (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-slate-600">
              {totpEnabled
                ? "Two-factor authentication is on for your account. If you've lost access to your authenticator app, reset it below - you'll need to confirm with your password or a current code first."
                : 'Two-factor authentication adds a second step to sign-in using an app on your phone. Turning it on is optional unless your organization requires it.'}
            </p>
            {error && <ErrorSummary error={error} className="mb-1" />}
            <Button onClick={start} disabled={submitting} className="w-fit justify-center">
              {submitting ? 'Starting…' : totpEnabled ? 'Reset my 2FA' : 'Enable 2FA'}
            </Button>
          </div>
        )}
        {step === 'enroll' && (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-slate-600">
              Scan this QR code with your authenticator app, then enter the 6-digit code it shows
              to confirm.
            </p>
            {enrollment && (
              <div
                className="mx-auto h-48 w-48 [&_svg]:h-full [&_svg]:w-full"
                dangerouslySetInnerHTML={{ __html: enrollment.qrSvg }}
              />
            )}
            {enrollment && (
              <p className="text-center text-xs text-slate-500">
                Can't scan it? Enter this code manually:{' '}
                <code className="font-mono">{enrollment.manualEntryKey}</code>
              </p>
            )}
            <FormField label="Verification code" htmlFor="security-code">
              <Input
                id="security-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </FormField>
            {error && <ErrorSummary error={error} className="mb-1" />}
            <div className="flex gap-2">
              <Button onClick={confirm} disabled={submitting} className="justify-center">
                {submitting ? 'Confirming…' : 'Confirm and finish'}
              </Button>
              <Button variant="ghost" onClick={() => setStep('idle')} className="justify-center">
                Cancel
              </Button>
            </div>
          </div>
        )}
        {step === 'recovery-codes' && (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-slate-600">
              Two-factor authentication is set up. Save these recovery codes somewhere safe - for
              your security, you will not be able to see them again.
            </p>
            <ul className="grid grid-cols-2 gap-2 rounded bg-slate-100 p-3 font-mono text-sm">
              {recoveryCodes.map((rc) => (
                <li key={rc}>{rc}</li>
              ))}
            </ul>
            <Button onClick={() => setStep('idle')} className="w-fit justify-center">
              I've saved these codes - done
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
